// src/Pages/Artists.js
import React, { useState, useEffect, useMemo } from 'react';
import pencilIcon from '../assets/icons/pencil.svg';
import { api } from '../api';

const inputStyle = {
  width: 'calc(100% - 20px)',
  marginBottom: '10px',
  padding: '10px',
  backgroundColor: '#333',
  color: 'white',
  border: 'none',
  borderRadius: '5px',
  fontFamily: 'inherit',
  boxSizing: 'border-box',
};

export default function Artists() {
  // Full dataset (we keep everything here so search can hit all artists)
  const [allArtists, setAllArtists] = useState([]);
  // UI-visible slice count
  const PAGE_SIZE = 50;
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  // Search box
  const [search, setSearch] = useState('');

  // Modal controls
  const [showModal, setShowModal] = useState(false);
  const [editingArtist, setEditingArtist] = useState(null);

  // Modal fields
  const [artistName, setArtistName] = useState('');
  const [emails, setEmails] = useState([]); // array of strings

  useEffect(() => {
    fetchArtists();
  }, []);

  const fetchArtists = async () => {
    try {
      const response = await api('/api/artists');
      const data = await response.json();
      setAllArtists(Array.isArray(data) ? data : []);
      setVisibleCount(PAGE_SIZE); // reset when refetching
    } catch (error) {
      console.error('Failed to fetch artists:', error);
      setAllArtists([]);
    }
  };

  // Derived: filtered by search (across ALL artists), then slice for display
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return allArtists;
    return allArtists.filter((a) =>
      (a?.name || '').toLowerCase().includes(q)
    );
  }, [allArtists, search]);

  const visible = useMemo(
    () => filtered.slice(0, visibleCount),
    [filtered, visibleCount]
  );

  // Reset visible count when search changes (show the first page of matches)
  useEffect(() => {
    setVisibleCount(PAGE_SIZE);
  }, [search]);

  const handleAddArtist = () => {
    setEditingArtist(null);
    setArtistName('');
    setEmails(['']); // start with one empty row for convenience
    setShowModal(true);
  };

  const handleEditArtist = (artist) => {
    setEditingArtist(artist);
    setArtistName(artist.name || '');
    setEmails(Array.isArray(artist.emails) && artist.emails.length ? artist.emails : ['']);
    setShowModal(true);
  };

  const addEmailRow = () => setEmails((prev) => [...prev, '']);
  const removeEmailRow = (idx) =>
    setEmails((prev) => prev.filter((_, i) => i !== idx));
  const updateEmailRow = (idx, val) =>
    setEmails((prev) => prev.map((e, i) => (i === idx ? val : e)));

  const normalizedEmails = () => {
    // Trim, drop empty, dedupe
    const seen = new Set();
    const out = [];
    for (const e of emails) {
      const v = String(e || '').trim();
      if (!v) continue;
      const key = v.toLowerCase();
      if (!seen.has(key)) {
        seen.add(key);
        out.push(v);
      }
    }
    return out;
  };

  const handleSaveArtist = async () => {
    if (!artistName.trim()) {
      alert('Artist name is required.');
      return;
    }

    const payload = { name: artistName.trim(), emails: normalizedEmails() };

    try {
      if (editingArtist) {
        await api(`/api/artists/${editingArtist._id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
      } else {
        await api('/api/artists', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
      }
      await fetchArtists();
      setShowModal(false);
    } catch (error) {
      console.error('Failed to save artist:', error);
      alert('Failed to save artist.');
    }
  };

  const handleDeleteArtist = async () => {
    if (!editingArtist) return;
    const confirmed = window.confirm(
      `Are you sure you want to delete artist "${editingArtist.name}"?`
    );
    if (!confirmed) return;

    try {
      await api(`/api/artists/${editingArtist._id}`, {
        method: 'DELETE',
      });
      await fetchArtists();
      setShowModal(false);
    } catch (error) {
      console.error('Failed to delete artist:', error);
      alert('Failed to delete artist.');
    }
  };

  return (
    <div style={{ padding: '20px' }}>
      <h1 className="header" style={{ marginTop: '10px', marginBottom: '12px' }}>
        Artists
      </h1>


      <button
        onClick={handleAddArtist}
        style={{
          backgroundColor: '#1e1e1e',
          color: 'white',
          border: 'none',
          padding: '15px 30px',
          borderRadius: '8px',
          cursor: 'pointer',
          marginBottom: '16px',
          display: 'flex',
          alignItems: 'center',
          fontWeight: 'bold',
        }}
      >
        <span style={{ fontSize: '24px', marginRight: '10px' }}>+</span>
        Create New Artist
      </button>

      {/* Search artists */}
<div style={{ marginBottom: 16 }}>
  <input
    type="text"
    placeholder="Search artists…"
    value={search}
    onChange={(e) => setSearch(e.target.value)}
    style={{
      width: '100%',
      maxWidth: 280,
      padding: 10,
      background: '#333',
      color: '#fff',
      border: 'none',
      borderRadius: 6,
      fontFamily: 'inherit',
    }}
  />
</div>

      {/* Modal for Adding/Editing Artist */}
      {showModal && (
        <div className="modal">
          <div
            className="modal-content"
            style={{
              width: '460px',
              padding: '20px',
              backgroundColor: '#1e1e1e',
              color: 'white',
              borderRadius: '10px',
              margin: 'auto',
            }}
          >
            <h2 style={{ marginTop: 0 }}>
              {editingArtist ? 'Edit Artist' : 'Add New Artist'}
            </h2>

            {/* Artist Name */}
            <label style={{ display: 'block', color: '#bbb', marginBottom: 6 }}>
              Artist Name
            </label>
            <input
              type="text"
              placeholder="Artist Name"
              value={artistName}
              onChange={(e) => setArtistName(e.target.value)}
              style={inputStyle}
            />

            {/* Emails (multi) */}
            <label style={{ display: 'block', color: '#bbb', margin: '10px 0 6px' }}>
              Emails
            </label>
            <div>
              {emails.map((val, idx) => (
                <div key={idx} style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                  <input
                    type="email"
                    placeholder="name@domain.com"
                    value={val}
                    onChange={(e) => updateEmailRow(idx, e.target.value)}
                    style={{ ...inputStyle, marginBottom: 0, width: '100%' }}
                  />
                  <button
                    onClick={() => removeEmailRow(idx)}
                    title="Remove"
                    style={{
                      background: '#333',
                      color: '#fff',
                      border: 'none',
                      borderRadius: 6,
                      padding: '6px 10px',
                      cursor: 'pointer',
                      height: 40,
                      alignSelf: 'center',
                    }}
                  >
                    ✕
                  </button>
                </div>
              ))}
              <button
                onClick={addEmailRow}
                style={{
                  background: '#555',
                  color: '#fff',
                  border: 'none',
                  borderRadius: 6,
                  padding: '6px 12px',
                  cursor: 'pointer',
                  fontWeight: 600,
                }}
              >
                + Add Email
              </button>
            </div>

            {/* Buttons Row */}
            <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
              <button
                onClick={handleSaveArtist}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#28a745',
                  color: 'white',
                  border: 'none',
                  borderRadius: '5px',
                  fontFamily: 'inherit',
                  cursor: 'pointer',
                }}
              >
                Save
              </button>
              <button
                onClick={() => setShowModal(false)}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#555',
                  color: 'white',
                  border: 'none',
                  borderRadius: '5px',
                  fontFamily: 'inherit',
                  cursor: 'pointer',
                }}
              >
                Cancel
              </button>

              {editingArtist && (
                <button
                  onClick={handleDeleteArtist}
                  style={{
                    padding: '10px 20px',
                    backgroundColor: '#ea5a54',
                    color: 'white',
                    border: 'none',
                    borderRadius: '5px',
                    fontFamily: 'inherit',
                    cursor: 'pointer',
                  }}
                >
                  Delete
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Artists Grid */}
      <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap' }}>
        {visible.map((artist) => (
          <div
            key={artist._id}
            style={{
              width: '260px',
              backgroundColor: '#1e1e1e',
              padding: '20px',
              borderRadius: '10px',
              color: 'white',
            }}
          >
            <h3 style={{ margin: '0 0 10px 0', fontSize: '1.2rem' }}>{artist.name}</h3>

            {/* Emails */}
            {Array.isArray(artist.emails) && artist.emails.length > 0 ? (
              <div style={{ marginBottom: 8 }}>
                {artist.emails.map((em, i) => (
                  <div
                    key={i}
                    style={{
                      fontSize: 12,
                      color: '#bbb',
                      wordBreak: 'break-all',
                    }}
                  >
                    {em}
                  </div>
                ))}
              </div>
            ) : (
              <div style={{ fontSize: 12, color: '#777', marginBottom: 8 }}>
                No emails
              </div>
            )}

            {/* Edit */}
            <button
              onClick={() => handleEditArtist(artist)}
              style={{
                background: 'transparent',
                border: 'none',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '5px',
              }}
            >
              <img
                src={pencilIcon}
                alt="Edit"
                style={{
                  width: '20px',
                  height: '20px',
                  filter: 'brightness(0) saturate(100%) invert(20%)',
                }}
              />
              <span style={{ color: '#999' }}>Edit</span>
            </button>
          </div>
        ))}
      </div>

      {/* Load more */}
      <div style={{ marginTop: 16, display: 'flex', justifyContent: 'center' }}>
        {visible.length < filtered.length && (
          <button
            onClick={() => setVisibleCount((c) => c + PAGE_SIZE)}
            style={{
              padding: '10px 20px',
              background: '#333',
              border: 'none',
              color: '#fff',
              borderRadius: 6,
              cursor: 'pointer',
            }}
          >
            Load more
          </button>
        )}
      </div>
    </div>
  );
}